import { createFileRoute } from "@tanstack/solid-router";
import { seo } from "~/utils/seo";

export const Route = createFileRoute("/_main/data-deletion")({
  head: () => ({
    meta: seo({
      title: "Data Deletion Request | ActShark",
      description:
        "Request deletion of your personal data from ActShark accounting software services.",
      path: "/data-deletion",
    }),
  }),
  component: DataDeletionPage,
});

function DataDeletionPage() {
  return (
    <main class="flex min-h-screen flex-col">
      <div class="mx-auto w-full max-w-4xl px-6 py-12 md:py-16 lg:py-20">
        <h1 class="mb-2 text-4xl font-bold">Data Deletion Request</h1>
        <p class="mb-8 text-sm text-muted-foreground">
          Last updated: Sep 4, 2026
        </p>

        <h2 class="mb-4 mt-8 text-2xl font-semibold">
          How to Request Data Deletion
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          You can request deletion of your personal data by contacting us via
          email at{" "}
          <a
            href="mailto:hello@actshark.com"
            class="text-blue-600 hover:underline"
          >
            hello@actshark.com
          </a>{" "}
          with the subject line "Data Deletion Request". To help us process your
          request, please include:
        </p>
        <ul class="mb-6 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>
            Your full name and the email address associated with your account
          </li>
          <li>A clear statement that you want your data deleted</li>
          <li>The ActShark services or features you used</li>
          <li>
            Any other information that could help us identify your account
          </li>
        </ul>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          You can also delete certain information yourself at any time by
          signing in to your account and using the account settings section.
          Deleting businesses or documents from within the Service removes that
          data from your workspace.
        </p>

        <h2 class="mb-4 mt-8 text-2xl font-semibold">What Happens Next</h2>
        <ol class="mb-6 ml-4 list-decimal space-y-2 text-muted-foreground">
          <li>We verify your identity to protect your account.</li>
          <li>
            We process your request within 30 days of verification, in
            accordance with our Privacy Policy.
          </li>
          <li>
            We send you a confirmation email once your data has been deleted.
          </li>
        </ol>

        <h2 class="mb-4 mt-8 text-2xl font-semibold">Important Information</h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          Some data may be retained for legal, security, or legitimate business
          purposes as described in our{" "}
          <a href="/privacy" class="text-blue-600 hover:underline">
            Privacy Policy
          </a>
          , including transaction records required for billing compliance,
          records related to ongoing disputes, and information we are legally
          required to keep.
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          After deletion, your data will be permanently removed from our active
          systems and cannot be recovered. Please export any books, documents,
          or reports you want to keep before requesting deletion.
        </p>

        <h2 class="mb-4 mt-8 text-2xl font-semibold">Questions?</h2>
        <p class="leading-relaxed text-muted-foreground">
          If you have questions about data deletion, contact us at{" "}
          <a
            href="mailto:hello@actshark.com"
            class="text-blue-600 hover:underline"
          >
            hello@actshark.com
          </a>
          .
        </p>
      </div>
    </main>
  );
}
